import React from 'react'
import { cn } from '@/utils/cn'

// Section status - dot colors
const SECTION_DOT_COLORS: Record<string, string> = {
  no_upload: 'bg-red-400',
  not_validated: 'bg-amber-400',
  validated: 'bg-emerald-400',
}

interface SectionSummary {
  section_id: number | null
  section_name: string
  image_count: number
  detection_count: number
  status: 'no_upload' | 'not_validated' | 'validated'
}

interface SessionSummary {
  session_id: number
  sections: SectionSummary[]
  total_detections: number
  overall_status: 'not_started' | 'incomplete_sections' | 'awaiting_validation' | 'complete'
}

interface MobileSessionCellProps {
  summary: SessionSummary | undefined
  isRetrospective?: boolean
  className?: string
}

/**
 * Displays session content (detections, sections) inside a mobile room card
 */
const MobileSessionCell: React.FC<MobileSessionCellProps> = ({
  summary,
  isRetrospective,
  className,
}) => {
  if (!summary) {
    return (
      <div className={cn('text-gray-400 text-sm', className)}>
        No data
      </div>
    )
  }

  return (
    <div className={cn('flex flex-col', className)}>
      {/* Total detections - prominent */}
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-bold text-gray-800">
          {summary.total_detections}
        </span>
        <span className="text-sm text-gray-500">items detected</span>
      </div>

      {/* Section breakdown */}
      {summary.sections.length > 0 && (
        <div className="mt-3 space-y-2">
          {summary.sections.map((sec, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 text-sm text-gray-600"
            >
              <span
                className={cn(
                  'w-2 h-2 rounded-full flex-shrink-0',
                  SECTION_DOT_COLORS[sec.status]
                )}
              />
              <span className="truncate flex-1">{sec.section_name}</span>
              <span className="text-gray-400 font-medium">
                {sec.detection_count}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Retrospective badge */}
      {isRetrospective && (
        <div className="mt-3">
          <span className="text-xs uppercase tracking-wide font-semibold text-amber-600 bg-amber-50 px-2 py-1 rounded">
            Retrospective
          </span>
        </div>
      )}
    </div>
  )
}

export default MobileSessionCell
